import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import dayjs from 'dayjs';
import { ApplicationConfigService } from 'services/application-config.service';
import { createRequestOption, mapAsBody } from 'app/shared/util/request-util';
import { getDocumentIdentifier, IDocument } from '../models/document.model';
import { DocumentCategory } from '../models/enums/document-category.enum';
import { API_ENDPOINTS } from '../models/enums/endpoints.enum';
import { ApiProviderService } from './api-provider.service';

export type EntityResponseType = HttpResponse<IDocument>;
export type EntityArrayResponseType = HttpResponse<IDocument[]>;

@Injectable({ providedIn: 'root' })
export class DocumentService {

  readonly #apiProvider = inject(ApiProviderService);
  readonly #applicationConfigService = inject(ApplicationConfigService);
  readonly #http = inject(HttpClient);

  private generateDocumentUrl = API_ENDPOINTS.DOCUMENT_GENERATE;
  private getDocumentUrl = API_ENDPOINTS.DOCUMENT_GET;
  private existDocumentUrl = API_ENDPOINTS.DOCUMENT_EXIST;


  protected resourceUrl: string= this.#applicationConfigService.getEndpointFor('api/documents');


  create(document: IDocument): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(document);
    return this.#http.post<IDocument>(this.resourceUrl, copy, { observe: 'response' }).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  update(document: IDocument): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(document);
    return this.#http
      .put<IDocument>(`${this.resourceUrl}/${getDocumentIdentifier(document)}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  find(id: number): Observable<EntityResponseType> {
    return this.#http.get<IDocument>(`${this.resourceUrl}/${id}`, { observe: 'response' }).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  query(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.#http
      .get<IDocument[]>(this.resourceUrl, {
        params: options,
        observe: 'response',
      })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  delete(id: number): Observable<HttpResponse<{}>> {
    return this.#http.delete(`${this.resourceUrl}/${id}`, { observe: 'response' });
  }

  protected convertDateFromClient(document: IDocument): IDocument {
    return Object.assign({}, document, {
      creationDate: document.creationDate?.isValid() ? document.creationDate.toJSON() : undefined,
    });
  }

  protected convertDateFromServer(res: EntityResponseType): EntityResponseType {
    if (res.body) {
      res.body.creationDate = res.body.creationDate ? dayjs(res.body.creationDate) : undefined;
    }
    return res;
  }

  protected convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    if (res.body) {
      res.body.forEach((document: IDocument) => {
        document.creationDate = document.creationDate ? dayjs(document.creationDate) : undefined;
      });
    }
    return res;
  }

  public generateDocument(category: DocumentCategory, templateId: number, relationId: number): Observable<any> {
    return this.#apiProvider.post<any>(this.generateDocumentUrl(category, templateId) + '?relationId=' + relationId, relationId)
  }

  public getDocument(category: DocumentCategory, relationId: number): Observable<Document> {
    return this.#apiProvider.publicGet<Document>(this.getDocumentUrl(category, relationId)).pipe(mapAsBody);
  }

  public existDocument(category: DocumentCategory): Observable<boolean> {
    return this.#apiProvider.publicGet<boolean>(this.existDocumentUrl(category)).pipe(mapAsBody);
  }
}
